import { MouseEventHandler, useEffect, useMemo, useRef, useState } from "react";
import type { KeyedMutator } from "swr";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useCreateNewSpecListVersion } from "@/hooks/useSpecLists";
import { RawSpecList, RawSpecValue, SpecList, SpecListVersion, SpecValue } from "@/types/specList";
import { deepEqual } from "@/lib/utils";
import { addPreviousValuesToSpecValues } from "../utils";
import { SelectedSpecValue } from "../SpecValueCell";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ModifiedSpecListTable } from "./ModifiedSpecListTable";
import { NewSpecForm } from "./NewSpecForm";
import { useRecipes } from "@/hooks/useRecipes";

type CreateNewSpecListVersionFormProps = {
  currentVersion: SpecListVersion;
  masterVersionSpecs: SpecValue[];
  onBack?: () => void;
  onOpenChange: (open: boolean) => void;
  refetch: KeyedMutator<RawSpecList>;
  setShowMasterSpecs: (state: boolean) => void;
  showMasterSpecs: boolean;
  specList: SpecList;
  specValues: SpecValue[];
};

export const NewSpecListVersionForm = ({
  currentVersion,
  masterVersionSpecs,
  onBack,
  onOpenChange,
  refetch,
  setShowMasterSpecs,
  showMasterSpecs,
  specList,
  specValues,
}: CreateNewSpecListVersionFormProps) => {
  const navigate = useNavigate();

  const { trigger: createSpecListVersion, isMutating: isCreatingSpecList } =
    useCreateNewSpecListVersion();

  const [selectedSpecs, setSelectedSpecs] = useState<SelectedSpecValue[]>([]);

  const { data: recipes } = useRecipes();
  const deviceClasses = [...new Set(selectedSpecs.map((sp) => sp.spec.deviceClass))];
  const conditions = [...new Set(selectedSpecs.map((sp) => sp.spec.condition ?? "None"))];

  const previousMasterSpecValuesRef = useRef<SpecValue[]>([]);

  const canSubmit = selectedSpecs.some((spec) => spec.selected);

  // Apply previousValues to current version, optionally including all master specs
  const versionWithPreviousValues = useMemo(() => {
    return {
      ...currentVersion,
      specValues: addPreviousValuesToSpecValues(
        masterVersionSpecs,
        specValues,
        showMasterSpecs, // Include unchanged master specs when toggle is on
      ),
    };
  }, [currentVersion, masterVersionSpecs, showMasterSpecs, specValues]);

  const masterSpecValues: SpecValue[] = versionWithPreviousValues.specValues;

  useEffect(() => {
    if (!deepEqual(previousMasterSpecValuesRef.current, masterSpecValues)) {
      const custom = masterSpecValues.map((spec) => ({
        ...spec,
        selected: true,
        customValues: undefined,
      }));
      // Preserve locally added specs (those not in master list)
      const masterIds = new Set(custom.map((s) => s.id));
      setSelectedSpecs((prev) => {
        const nonMasterSpecs = prev.filter((s) => !masterIds.has(s.id));
        return [...custom, ...nonMasterSpecs];
      });

      previousMasterSpecValuesRef.current = masterSpecValues;
    }
  }, [masterSpecValues]);

  const displaySpecs = useMemo(() => {
    const selectedById = new Map(selectedSpecs.map((s) => [s.id, s]));

    const mergedMaster = versionWithPreviousValues.specValues.map((spec) => ({
      ...spec,
      selected: selectedById.get(spec.id)?.selected || false,
      customValues: selectedById.get(spec.id)?.customValues || undefined,
    }));

    const masterIds = new Set(mergedMaster.map((s) => s.id));
    const addedSpecs = selectedSpecs.filter((s) => !masterIds.has(s.id));

    return [...mergedMaster, ...addedSpecs];
  }, [versionWithPreviousValues.specValues, selectedSpecs]);

  const handleAddSpec = async (data: any) => {
    const newSpec: SelectedSpecValue = {
      id: "",
      specId: "",
      specListVersionId: currentVersion.id,
      lsl: data.lsl,
      tgt: data.tgt,
      usl: data.usl,
      jmpYmin: data.jmpYmin,
      jmpYmax: data.jmpYmax,
      jmpYinc: data.jmpYinc,
      libMedian: data.libMedian,
      libMean: data.libMean,
      libStd: data.libStd,
      libQ10: data.libQ10,
      libQ90: data.libQ90,
      libN: data.libN,
      isMasterValue: false,
      selected: true, // immediately selected
      customValues: undefined,
      spec: {
        id: "",
        deviceClass: data.deviceClass,
        name: data.reportName,
        reportName: data.reportName,
        condition: data.condition,
        legacyNames: data.legacyNames ?? "",
        dutNames: data.dutNames ?? "",
        recipe: data.recipe,
      },
    };

    setSelectedSpecs((prev) => [...prev, newSpec]);
  };

  const applyCustomValuesToSpecValue = (specValue: SelectedSpecValue): RawSpecValue => {
    const customValueKeys = Object.keys(
      specValue.customValues ?? {},
    ) as unknown as (keyof SpecValue)[];

    const updatedSpecValue = { ...specValue };

    customValueKeys.forEach((key) => {
      const valueToApply = specValue.customValues![key];
      // @ts-expect-error not sure why it complains about a "never" type here
      updatedSpecValue[key] = valueToApply;
    });

    return {
      id: updatedSpecValue.id,
      spec_id: updatedSpecValue.specId,
      spec_version_id: updatedSpecValue.specListVersionId,
      jmp_yinc: updatedSpecValue.jmpYinc,
      jmp_ymax: updatedSpecValue.jmpYmax,
      jmp_ymin: updatedSpecValue.jmpYmin,
      lib_mean: updatedSpecValue.libMean,
      lib_median: updatedSpecValue.libMedian,
      lib_n: updatedSpecValue.libN,
      lib_q10: updatedSpecValue.libQ10,
      lib_q90: updatedSpecValue.libQ90,
      lib_std: updatedSpecValue.libStd,
      lsl: updatedSpecValue.lsl,
      tgt: updatedSpecValue.tgt,
      usl: updatedSpecValue.usl,
      spec: {
        id: updatedSpecValue.spec.id,
        device_class: updatedSpecValue.spec.deviceClass,
        name: updatedSpecValue.spec.name,
        report_name: updatedSpecValue.spec.reportName,
        condition: updatedSpecValue.spec.condition,
        legacy_names: updatedSpecValue.spec.legacyNames,
        dut_names: updatedSpecValue.spec.dutNames,
        recipe: {
          id: String(updatedSpecValue.spec.recipe?.id ?? ""),
          name: updatedSpecValue.spec.recipe?.name ?? "",
          description: updatedSpecValue.spec.recipe?.description ?? "",
        },
      },
    };
  };

  const handleSubmit: MouseEventHandler<HTMLButtonElement> = async () => {
    try {
      const selectedSpecsData = selectedSpecs.filter((spec) => spec.selected);
      const specValuesToPersist = selectedSpecsData.map(applyCustomValuesToSpecValue);
      await createSpecListVersion(
        {
          id: specList.id,
          spec_values: specValuesToPersist,
        },
        {
          onError: (error) => {
            toast.error(error.message);
          },
          onSuccess: async (data) => {
            const newSpecListVersionId = data.versions.at(-1)?.id;
            onOpenChange(false);
            resetForm();
            await refetch();
            navigate(`/spec-lists/${specList.id}/version/${newSpecListVersionId}`);
          },
        },
      );
    } catch (error) {
      console.error(error);
    }
  };

  const resetForm = () => {
    setSelectedSpecs([]);
  };

  const onCancel = () => {
    onOpenChange(false);
    resetForm();
  };

  const isMasterSpecList = specList.type === "master";

  return (
    <div className="h-full flex flex-col">
      <div className="space-y-4 flex-1 min-h-0 flex flex-col">
        <Card className="flex-1 flex flex-col">
          <Tabs defaultValue="modify" className="w-full h-full flex flex-col">
            {isMasterSpecList ? (
              <CardHeader className="pb-0">
                <TabsList>
                  <TabsTrigger value="modify">Modify Specs</TabsTrigger>
                  <TabsTrigger value="add">Add Spec</TabsTrigger>
                </TabsList>
              </CardHeader>
            ) : null}
            <CardContent>
              <TabsContent value="modify" className={isMasterSpecList ? "flex-1 mt-4" : "flex-1"}>
                <ModifiedSpecListTable
                  setSelectedSpecs={setSelectedSpecs}
                  deviceClasses={deviceClasses}
                  conditions={conditions}
                  specList={specList}
                  displaySpecs={displaySpecs}
                  setShowMasterSpecs={setShowMasterSpecs}
                  showMasterSpecs={showMasterSpecs}
                />
              </TabsContent>

              <TabsContent value="add" className="mt-4 flex-1">
                <NewSpecForm
                  deviceClasses={deviceClasses}
                  conditions={conditions}
                  recipes={recipes}
                  onSubmit={handleAddSpec}
                />
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        <div className="pt-4 border-t bg-background">
          <div className="flex justify-between">
            {onBack && (
              <Button type="button" variant="ghost" onClick={onBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!canSubmit || isCreatingSpecList}>
                Create New Version ({selectedSpecs.filter((s) => s.selected).length} specs)
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
